import { memo, useCallback, useMemo, useState } from "react";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import { Colors } from "../../../../utils/colors";
import {
  DMAutomation,
  DMAutomationPlatform,
  DMAutomationTrigger,
  InstagramTrigger,
  OnboardingFlowType,
  DMAutomationTriggerContentType,
} from "@markit/common.types";
import { makeEmptyDMAutomationTrigger } from "@markit/common.utils";
import { useSelector } from "react-redux";
import {
  getAccountSlice,
  getIncompleteOnboardingFlows,
} from "../../../../redux/slices/accountSlice";
import OnboardingTourStep from "../../../Onboarding/OnboardingTourStep";
import { SocialPlatformIconItem } from "../../../DisplayItem/AutoDM/SocialPlatformIconItem";
import SearchBoxContainer from "../../../Containers/SearchBoxContainer";
import ListTabs from "../../../DropdownsAndTabs/ListTabs";
import { hasSocialAccountConnection } from "../../../../utils/socialAccountUtils";

// Index-aligned with the ListTabs labels below.
const PLATFORM_TAB_ORDER = [
  DMAutomationPlatform.INSTAGRAM,
  DMAutomationPlatform.FACEBOOK,
];

type TriggerOption = {
  key: string;
  id: InstagramTrigger;
  header: string;
  title: string;
  description: string;
};

// Per platform, since the trigger set differs slightly (Facebook Pages don't have
// Reels within Markit's build, and the "coming soon" placeholder is platform-flavored
// for parity with the real, working card above it)
const TRIGGER_OPTIONS_BY_PLATFORM: Record<
  DMAutomationPlatform,
  TriggerOption[]
> = {
  [DMAutomationPlatform.INSTAGRAM]: [
    {
      key: InstagramTrigger.COMMENT_POST_REEL,
      id: InstagramTrigger.COMMENT_POST_REEL,
      header: "Post or Reel Comments",
      title: "User comments on a Post or Reel",
      description:
        "When a user comments on all, next, or a specific post or reel with any or specific keywords.",
    },
  ],
  [DMAutomationPlatform.FACEBOOK]: [
    {
      key: InstagramTrigger.COMMENT_POST_REEL,
      id: InstagramTrigger.COMMENT_POST_REEL,
      header: "Post Comments",
      title: "User comments on a Post",
      description:
        "When a user comments on all, next, or a specific post with any or specific keywords.",
    },
  ],
};

const REPLY_TRIGGER_OPTIONS: TriggerOption[] = [
  {
    key: InstagramTrigger.REPLY_DM,
    id: InstagramTrigger.REPLY_DM,
    header: "DM Keywords",
    title: "User sends you a DM",
    description: "When a user DMs you with specific keywords.",
  },
  {
    key: InstagramTrigger.REPLY_STORY,
    id: InstagramTrigger.REPLY_STORY,
    header: "Story Replies",
    title: "User replies to a Story",
    description:
      "When a user replies to any, next, or a specific story with any or specific keywords.",
  },
];

type SelectAutomationTriggerModalProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  existingDmAutomation?: DMAutomation;
  onSelectTrigger: (
    trigger: DMAutomationTrigger,
    platform: DMAutomationPlatform
  ) => void;
};

const SelectAutomationTriggerModal = (
  props: SelectAutomationTriggerModalProps
) => {
  const { showModal, setShowModal, existingDmAutomation, onSelectTrigger } =
    props;
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountSlice);
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const [searchTerm, setSearchTerm] = useState("");

  // Only offer platforms the creator actually has an account connected for -
  // there's no point letting them pick a trigger for a platform with nothing to trigger from.
  const availablePlatforms = useMemo(
    () =>
      PLATFORM_TAB_ORDER.filter((platform) =>
        platform === DMAutomationPlatform.FACEBOOK
          ? hasSocialAccountConnection(facebookPages)
          : hasSocialAccountConnection(instagramAccounts)
      ),
    [facebookPages, instagramAccounts]
  );

  const [selectedPlatform, setSelectedPlatform] =
    useState<DMAutomationPlatform>(
      existingDmAutomation?.platform ??
        availablePlatforms[0] ??
        DMAutomationPlatform.INSTAGRAM
    );
  const showPlatformTabs = useMemo(
    () => !existingDmAutomation && availablePlatforms.length > 1,
    [existingDmAutomation, availablePlatforms.length]
  );

  const selectedPlatformIndex = useMemo(
    () => PLATFORM_TAB_ORDER.indexOf(selectedPlatform),
    [selectedPlatform]
  );

  const hasAutoDMTour = useMemo(
    () =>
      incompleteOnboardingFlows.some(
        (flow) => flow.id === OnboardingFlowType.INSTAGRAM_DM
      ),
    [incompleteOnboardingFlows]
  );

  const triggerOptions = useMemo(() => {
    const baseOptions = TRIGGER_OPTIONS_BY_PLATFORM[selectedPlatform];
    const options =
      selectedPlatform === DMAutomationPlatform.INSTAGRAM
        ? [...baseOptions, ...REPLY_TRIGGER_OPTIONS]
        : baseOptions;
    if (!searchTerm.trim()) {
      return options;
    }
    const lower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.header.toLowerCase().includes(lower) ||
        option.title.toLowerCase().includes(lower)
    );
  }, [selectedPlatform, searchTerm]);

  const selectAutomationTrigger = useCallback(
    (triggerType: InstagramTrigger) => {
      const trigger = makeEmptyDMAutomationTrigger(
        accountData.uid,
        existingDmAutomation?.id ?? "",
        triggerType,
        "",
        selectedPlatform
      );
      // Reply to DM always uses its sentinel content id; comment and story triggers pick theirs in the builder
      if (triggerType === InstagramTrigger.REPLY_DM) {
        trigger.contentId = DMAutomationTriggerContentType.ALL_DM;
      }
      onSelectTrigger(trigger, selectedPlatform);
      setShowModal(false);
    },
    [
      accountData.uid,
      existingDmAutomation?.id,
      onSelectTrigger,
      selectedPlatform,
      setShowModal,
    ]
  );

  const TriggerOptionCard = useCallback(
    (option: TriggerOption) => (
      <div
        key={option.key}
        className="AlignedRow"
        style={{
          border: `0.5px solid ${Colors.GRAY2}`,
          borderRadius: 8,
          padding: 14,
          gap: 10,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        onClick={() => selectAutomationTrigger(option.id)}
      >
        <SocialPlatformIconItem
          platform={selectedPlatform}
          size={26}
          noBorder
        />
        <div className="ColumnNormal" style={{ gap: 4, paddingRight: 24 }}>
          <span className="bodySubtext">{option.header}</span>
          <span className="sectionTitle">{option.title}</span>
          <span className="smallBodySubtext">{option.description}</span>
        </div>
      </div>
    ),
    [selectAutomationTrigger, selectedPlatform]
  );

  return (
    <PopupModalContainer
      altPadding={0}
      positionOnTop={150}
      valueComp={
        <div
          className="ColumnNormal onboarding-instagram-dm-selecttrigger-target"
          style={{ gap: 14, paddingInline: 14 }}
        >
          <span className="sectionTitle">Start Automation When...</span>
          <div className="AlignedRowSpaced" style={{ gap: 14 }}>
            {showPlatformTabs ? (
              <ListTabs
                tabLabels={["Instagram", "Facebook"]}
                tabIcons={[
                  <SocialPlatformIconItem
                    key="instagram-icon"
                    platform={DMAutomationPlatform.INSTAGRAM}
                    size={16}
                    noBorder
                  />,
                  <SocialPlatformIconItem
                    key="facebook-icon"
                    platform={DMAutomationPlatform.FACEBOOK}
                    size={16}
                    noBorder
                  />,
                ]}
                tabNumbers={[-1, -1]}
                selectedValue={selectedPlatformIndex}
                onChange={(index) =>
                  setSelectedPlatform(PLATFORM_TAB_ORDER[index])
                }
                altColor={Colors.BLACK}
              />
            ) : null}
            <SearchBoxContainer
              placeholder="Search"
              onChange={(e) => setSearchTerm(e.target.value)}
              containerStyles={{ marginTop: 0, width: 280, height: 36 }}
            />
          </div>
          <div className="ColumnNormal" style={{ gap: 10, overflowY: "auto" }}>
            {triggerOptions.map((option) => TriggerOptionCard(option))}
          </div>
          {!existingDmAutomation ? (
            <OnboardingTourStep
              key="instagram-dm-tour-step-1"
              flowType={OnboardingFlowType.INSTAGRAM_DM}
              target=".onboarding-instagram-dm-selecttrigger-target"
              content={
                <>
                  Auto DM lets you automatically send DMs to people who engage
                  with your social media content, like when users comment on a
                  post, reply to a story, or DM you a keyword.
                </>
              }
              stepNumber={1}
              placement="bottom"
              mountDelayMs={300}
            />
          ) : null}
        </div>
      }
      noExit
      noDivider
      closeOnOutsidePress={!hasAutoDMTour}
      closeModal={() => setShowModal(false)}
      hideModal={!showModal}
      modalContentWidth={PopupModalContainerWidth.WIDE}
    />
  );
};

export default memo(SelectAutomationTriggerModal);
